import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Loader2, MessageSquare, Radio, Calendar as CalIcon, Users,
  ExternalLink, Pencil, RefreshCw, Clock, Sparkles, ChevronLeft, ChevronRight,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JOIN_WINDOW_MIN = 15;    // matches backend JOIN_WINDOW_BEFORE_MIN
const SOON_WINDOW_MIN = 60;    // "Starting soon" ribbon threshold
const PAGE_SIZE = 9;           // 9 cards per page (3x3 on xl)

const IST_TZ = 'Asia/Kolkata';
const fmtISTLong = (iso) => new Intl.DateTimeFormat('en-IN', {
  weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  hour: '2-digit', minute: '2-digit', hour12: false, timeZone: IST_TZ,
}).format(new Date(iso));

const humanizeGap = (ms) => {
  if (ms <= 0) return 'now';
  const totalMin = Math.floor(ms / 60000);
  const days = Math.floor(totalMin / (60 * 24));
  const hours = Math.floor((totalMin % (60 * 24)) / 60);
  const mins = totalMin % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

const classifyClass = (cls, nowMs) => {
  const startMs = new Date(cls.start_time).getTime();
  const endMs = startMs + (cls.duration_minutes || 60) * 60000;
  const gapMs = startMs - nowMs;
  const inJoinWindow = gapMs <= JOIN_WINDOW_MIN * 60000 && nowMs < endMs;
  const startingSoon = cls.status === 'scheduled' && gapMs > 0 && gapMs <= SOON_WINDOW_MIN * 60000;
  return { startMs, endMs, gapMs, inJoinWindow, startingSoon };
};

const statusChipClasses = {
  scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
  live: 'bg-emerald-100 text-emerald-800 border-emerald-300 animate-pulse',
  completed: 'bg-gray-100 text-gray-600 border-gray-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
};

const UpcomingClassesTab = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isFullAdmin = user?.role === 'admin';

  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hostFilter, setHostFilter] = useState(isFullAdmin ? 'all' : 'mine');
  const [nowMs, setNowMs] = useState(Date.now());
  const [page, setPage] = useState(1);

  // Refresh "now" every 30s so countdowns tick down and ribbons update
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      // For sub-admins the backend rewrites scope='mine' regardless, but we
      // still send it explicitly for admins so their "All / Only me" toggle works.
      const scope = hostFilter === 'mine' ? 'mine' : 'upcoming';
      const { data } = await axios.get(`${API}/admin/live-classes`, { params: { scope } });
      // Extra safety: filter out completed & cancelled + past classes client-side
      const now = Date.now();
      const upcoming = (data || []).filter((c) => {
        if (c.status === 'cancelled' || c.status === 'completed') return false;
        const endMs = new Date(c.start_time).getTime() + (c.duration_minutes || 60) * 60000;
        return endMs > now;
      });
      setClasses(upcoming);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load classes');
    } finally {
      setLoading(false);
    }
  }, [hostFilter]);

  useEffect(() => { load(); }, [load]);

  // Sort: starting-soon first (by proximity), then chronological
  const sortedClasses = useMemo(() => {
    const withMeta = classes.map((c) => ({ cls: c, meta: classifyClass(c, nowMs) }));
    withMeta.sort((a, b) => {
      if (a.meta.startingSoon !== b.meta.startingSoon) return a.meta.startingSoon ? -1 : 1;
      return a.meta.startMs - b.meta.startMs;
    });
    return withMeta;
  }, [classes, nowMs]);

  const soonCount = sortedClasses.filter((x) => x.meta.startingSoon).length;

  // Reset to page 1 whenever filter or dataset changes (avoids stale "page 4" on empty result)
  useEffect(() => { setPage(1); }, [hostFilter, classes.length]);

  const totalPages = Math.max(1, Math.ceil(sortedClasses.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = Math.min(pageStart + PAGE_SIZE, sortedClasses.length);
  const paginated = sortedClasses.slice(pageStart, pageEnd);

  return (
    <Card data-testid="upcoming-classes-panel">
      <CardContent className="pt-6">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <h3 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#0A7EA4]" />
              Upcoming Classes
              <span className="text-xs text-gray-400 font-normal">({sortedClasses.length})</span>
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {isFullAdmin
                ? 'All scheduled and live classes across grades.'
                : 'Your assigned upcoming classes.'}
              {soonCount > 0 && (
                <span className="ml-2 text-amber-700 font-medium" data-testid="upcoming-starting-soon-count">
                  · {soonCount} starting in the next hour
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Host filter: full-admin only */}
            {isFullAdmin && (
              <Tabs value={hostFilter} onValueChange={setHostFilter}>
                <TabsList className="h-9" data-testid="upcoming-host-filter">
                  <TabsTrigger value="all" className="text-xs" data-testid="upcoming-host-all">
                    All classes
                  </TabsTrigger>
                  <TabsTrigger value="mine" className="text-xs" data-testid="upcoming-host-mine">
                    Only me
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={load}
              className="h-9"
              data-testid="upcoming-refresh-btn"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
            </Button>
          </div>
        </div>

        {/* Body */}
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading upcoming classes...
          </div>
        ) : sortedClasses.length === 0 ? (
          <div className="text-center py-10 border border-dashed rounded-lg" data-testid="upcoming-empty-state">
            <CalIcon className="w-8 h-8 mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-600">No upcoming classes {hostFilter === 'mine' ? 'assigned to you' : 'scheduled'}.</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {paginated.map(({ cls, meta }) => (
                <UpcomingClassCard
                  key={cls.id}
                  cls={cls}
                  meta={meta}
                  isFullAdmin={isFullAdmin}
                  onNavigateQna={() => navigate(`/live-classes/${cls.id}/questions`)}
                  onNavigateHost={() => navigate(`/admin/live-classes/${cls.id}/host`)}
                  onEdit={() => {
                    // Switch to Classes tab and open the scheduler dialog for this class.
                    // Keeping this navigation simple — deep-link params can be a future
                    // enhancement.
                    toast.info('Open "Classes" tab to edit this class.');
                  }}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div
                className="flex items-center justify-between mt-5 pt-4 border-t"
                data-testid="upcoming-pagination"
              >
                <p className="text-xs text-gray-500 tabular-nums" data-testid="upcoming-page-range">
                  Showing {pageStart + 1}–{pageEnd} of {sortedClasses.length}
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="upcoming-pagination-prev"
                  >
                    <ChevronLeft className="w-3.5 h-3.5 mr-1" />
                    Previous
                  </Button>
                  {Array.from({ length: totalPages }).map((_, i) => {
                    const n = i + 1;
                    const isActive = n === currentPage;
                    // Collapse middle range when many pages: show first, last, ±1 around current
                    const showNumber =
                      n === 1 ||
                      n === totalPages ||
                      Math.abs(n - currentPage) <= 1;
                    const showEllipsisBefore = n === currentPage - 2 && n > 2;
                    const showEllipsisAfter = n === currentPage + 2 && n < totalPages - 1;
                    if (showEllipsisBefore || showEllipsisAfter) {
                      return <span key={`el-${n}`} className="px-1 text-gray-400 text-xs">…</span>;
                    }
                    if (!showNumber) return null;
                    return (
                      <Button
                        key={n}
                        variant={isActive ? 'default' : 'ghost'}
                        size="sm"
                        className={`h-8 w-8 p-0 text-xs tabular-nums ${isActive ? 'bg-[#0A7EA4] hover:bg-[#086A8A] text-white' : ''}`}
                        onClick={() => setPage(n)}
                        data-testid={`upcoming-page-${n}`}
                      >
                        {n}
                      </Button>
                    );
                  })}
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8"
                    disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    data-testid="upcoming-pagination-next"
                  >
                    Next
                    <ChevronRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const UpcomingClassCard = ({ cls, meta, isFullAdmin, onNavigateQna, onNavigateHost, onEdit }) => {
  const statusStyle = statusChipClasses[cls.status] || statusChipClasses.scheduled;
  const gapText = humanizeGap(meta.gapMs);
  const qnaCount = cls.qna_count || 0;
  const qnaOpen = cls.qna_open_count || 0;

  return (
    <div
      className={`relative rounded-lg border bg-white shadow-sm hover:shadow-md transition overflow-hidden ${
        meta.startingSoon ? 'border-amber-400 ring-1 ring-amber-300' : 'border-gray-200'
      }`}
      data-testid={`upcoming-class-card-${cls.id}`}
    >
      {meta.startingSoon && (
        <div
          className="absolute top-0 right-0 left-0 bg-amber-400 text-amber-900 text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 text-center"
          data-testid="starting-soon-ribbon"
        >
          Starting soon · in {gapText}
        </div>
      )}

      <div className={`p-4 ${meta.startingSoon ? 'pt-7' : ''}`}>
        {/* Title + status */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <h4 className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2 flex-1">
            {cls.title}
          </h4>
          <Badge className={`text-[10px] whitespace-nowrap capitalize ${statusStyle}`} data-testid={`class-status-${cls.id}`}>
            {cls.status === 'live' && <Radio className="inline w-3 h-3 mr-1" />}
            {cls.status}
          </Badge>
        </div>

        {/* Host + start */}
        <div className="space-y-1 text-xs text-gray-600 mb-3">
          <p className="flex items-center gap-1.5">
            <Users className="w-3.5 h-3.5 text-gray-400" />
            <span className="truncate">{cls.host_user_name || '—'}</span>
          </p>
          <p className="flex items-center gap-1.5 tabular-nums">
            <CalIcon className="w-3.5 h-3.5 text-gray-400" />
            {fmtISTLong(cls.start_time)}
            <span className="text-gray-400">· {cls.duration_minutes}m</span>
          </p>
          {!meta.startingSoon && cls.status === 'scheduled' && meta.gapMs > 0 && (
            <p className="flex items-center gap-1.5 text-gray-500">
              <Clock className="w-3.5 h-3.5 text-gray-400" />
              in {gapText}
            </p>
          )}
        </div>

        {/* Grade & MMD pills */}
        {((Array.isArray(cls.grades) && cls.grades.length > 0) || (Array.isArray(cls.mmd_locations) && cls.mmd_locations.length > 0)) && (
          <div className="flex flex-wrap gap-1 mb-3">
            {(cls.grades || []).slice(0, 3).map((g) => (
              <span key={g} className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200">
                {g}
              </span>
            ))}
            {(cls.grades || []).length > 3 && (
              <span className="text-[10px] text-gray-500">+{cls.grades.length - 3}</span>
            )}
            {(cls.mmd_locations || []).slice(0, 3).map((m) => (
              <span
                key={m}
                className="text-[10px] px-1.5 py-0.5 rounded bg-slate-50 text-slate-700 border border-slate-300"
                data-testid={`admin-cls-mmd-${m}`}
              >
                {m}
              </span>
            ))}
            {(cls.mmd_locations || []).length > 3 && (
              <span className="text-[10px] text-slate-500">+{cls.mmd_locations.length - 3}</span>
            )}
          </div>
        )}

        {/* Q&A badge */}
        <div className="mb-3 flex items-center justify-between text-xs">
          <div className="flex items-center gap-1.5">
            <MessageSquare className="w-3.5 h-3.5 text-violet-500" />
            <span className="tabular-nums font-medium text-gray-700">{qnaCount}</span>
            <span className="text-gray-500">question{qnaCount === 1 ? '' : 's'}</span>
            {qnaOpen > 0 && (
              <span className="text-amber-700 font-medium" data-testid={`class-qna-open-${cls.id}`}>
                · {qnaOpen} unanswered
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-1.5 pt-1 border-t border-gray-100">
          <Button
            size="sm"
            variant="outline"
            className="h-7 text-xs flex-1 min-w-fit"
            onClick={onNavigateQna}
            data-testid={`view-qna-btn-${cls.id}`}
          >
            <MessageSquare className="w-3 h-3 mr-1" /> View Q&amp;A
          </Button>
          {meta.inJoinWindow && (
            <Button
              size="sm"
              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white flex-1 min-w-fit"
              onClick={onNavigateHost}
              data-testid={`join-host-btn-${cls.id}`}
            >
              <ExternalLink className="w-3 h-3 mr-1" /> Join / Host
            </Button>
          )}
          {isFullAdmin && !meta.inJoinWindow && (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-xs"
              onClick={onEdit}
              data-testid={`edit-class-btn-${cls.id}`}
            >
              <Pencil className="w-3 h-3 mr-1" /> Edit
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default UpcomingClassesTab;
